import { FC, useMemo, useState } from 'react';

import { ShareClass, useLocale } from '../../../../../hooks/index';
import { stakeholderTypeTranslation } from '../../../../../translations/stakeholder-translation.util';
import {
  CapTableOrdering,
  Column,
  DetailsSortVariant,
  StakeholderColumns,
  StakeholderStats,
  ViewBy,
} from '../../../../../types/snapshot.types';
import { filterByAllFields } from '../../../../../utils/filter-by-all-fields.util';
import { toRound } from '../../../../../utils/get-rounded-number.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { formatDividendAmount } from '../../../../dividends/utils/format-dividend-amount.util';
import { CapTableExpandedRowsPanel } from '../../../components/cap-table-table-animations';
import { sortStakeholdersByOrdering } from '../../../utils/sort-cap-table-rows.util';
import {
  DEFAULT_CAP_TABLE_ORDERING,
  getShareClasses,
} from '../../snapshot-view-by/snapshot-view-by.types';
import { TableRow } from '../table-row';
import { LoadMoreItem } from './load-more-item';
import { useCapTableGroupReveal } from './use-cap-table-group-reveal.hook';

type StakeholderTableItemProps = {
  allShareClassNames: string[];
  className?: string;
  /**
   * Supplied by callers that already know it — the shared public page rewrites share class ids and
   * has no session to look the default one up with, so a fetched id would never match its rows.
   */
  defaultShareClassId?: string;
  fields: Record<StakeholderColumns, null | number | number[] | string | string[]>;
  /** The Ordering tab's settings; the sub-rows of one group all share a type, so it is the
   * "within each type" rule that decides their order. */
  ordering?: CapTableOrdering;
  trClassName?: string;
  viewBy: Record<ViewBy, boolean>;
  visibleColumns: Column[];
} & Optional<{
  search: string;
  stakeholders: StakeholderStats[];
}>;

export const StakeholderTableItem: FC<StakeholderTableItemProps> = ({
  viewBy,
  visibleColumns,
  fields,
  stakeholders = [],
  className,
  trClassName,
  allShareClassNames,
  defaultShareClassId: providedShareClassId,
  ordering = DEFAULT_CAP_TABLE_ORDERING,
  search = '',
}) => {
  const searchResult = useMemo(
    () => sortStakeholdersByOrdering(filterByAllFields(stakeholders, search), ordering),
    [ordering, stakeholders, search],
  );

  const [isExpanded, setExpanded] = useState(false);
  const [isExpandedVisual, setExpandedVisual] = useState(false);
  const {
    handleExtraCollapseComplete,
    handleLoadMore,
    handleShowLess,
    isExtraRowsOpen,
    loadCount,
    pinnedRowCount,
    renderedCount,
    showLess,
    showRevealControl,
  } = useCapTableGroupReveal(searchResult.length, isExpanded);

  const visibleStakeholders = searchResult.slice(0, renderedCount);

  const viewByAll = viewBy.stakeholders && viewBy.type;

  const { shareClass } = ShareClass.useDefaultShareClass(isExpanded && !providedShareClassId);

  const defaultShareClassId = providedShareClassId || shareClass?.id || '';

  const { messagesLocale } = useLocale();

  const isStakeholdersEmpty = stakeholders.length === 0;

  return (
    <>
      <TableRow
        disableExpand={isStakeholdersEmpty}
        fields={fields}
        handleExpandRow={setExpanded}
        isExpanded={isExpanded}
        isExpandedRow={false}
        isExpandedVisual={isExpandedVisual}
        tdClassName={className}
        trClassName={trClassName}
        type={DetailsSortVariant.BY_STAKEHOLDER}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
      {!isStakeholdersEmpty && (
        <CapTableExpandedRowsPanel
          footer={
            showRevealControl && (
              <LoadMoreItem
                loadCount={loadCount}
                onLoadMore={handleLoadMore}
                onShowLess={handleShowLess}
                showLess={showLess}
                viewByAll={Boolean(viewByAll)}
              />
            )
          }
          isExpanded={isExpanded}
          isExtraRowsOpen={isExtraRowsOpen}
          onExpandedVisualChange={setExpandedVisual}
          onExtraCollapseComplete={handleExtraCollapseComplete}
          pinnedRowCount={pinnedRowCount}
        >
          {visibleStakeholders.map(
            (
              {
                diluted,
                dilutedPercentage,
                issued,
                issuedPercentage,
                name,
                type,
                votingPercentage,
                shareClasses,
                votingRight,
                invested,
                dividends,
                avgSharePrice,
                converted,
                convertedPercentage,
              },
              i,
            ) => {
              const { defaultShareClass, otherShareClasses } = getShareClasses(
                defaultShareClassId,
                shareClasses,
              );

              const otherShareClassesMap = otherShareClasses
                .sort((a, b) => a.name.localeCompare(b.name))
                .reduce<Record<string, (typeof otherShareClasses)[0]>>((acc, curr) => {
                  acc[curr.name] = curr;
                  return acc;
                }, {});

              return (
                <TableRow
                  fields={{
                    [Column.NAME]: name,
                    [Column.STAKEHOLDER_NAME]: name,
                    [Column.TYPE]: messagesLocale[stakeholderTypeTranslation[type]],
                    [Column.ISSUED]: toRound(issuedPercentage * 100),
                    [Column.DILUTED]: toRound(dilutedPercentage * 100),
                    [Column.VOTING]: toRound(votingPercentage * 100),
                    [Column.NO_ISSUED]: issued,
                    [Column.DILUTED_SHARES]: diluted.toLocaleString('en-US'),
                    [Column.COMMON_STOCK]: toNumber(defaultShareClass?.issued),
                    [Column.OTHER_SHARE_CLASSES]: allShareClassNames.map((name) => {
                      return otherShareClassesMap[name] ? otherShareClassesMap[name].issued : 0;
                    }),
                    [Column.VOTING_RIGHTS]: votingRight.toLocaleString('en-US'),
                    [Column.INVESTMENT]: invested,
                    [Column.DIVIDENDS]: formatDividendAmount(toNumber(dividends)),
                    [Column.AVERAGE_SHARE_PRICE]: toRound(avgSharePrice),
                    [Column.CONVERTED_SHARES]: converted,
                    [Column.CONVERTED_PERCENTAGE]: `${toRound(toNumber(convertedPercentage) * 100)}`,
                  }}
                  isExpandedRow={true}
                  isLastInExpandedGroup={!showRevealControl && i === visibleStakeholders.length - 1}
                  key={i}
                  type={DetailsSortVariant.BY_STAKEHOLDER}
                  viewBy={viewBy}
                  visibleColumns={visibleColumns}
                />
              );
            },
          )}
        </CapTableExpandedRowsPanel>
      )}
    </>
  );
};
